import { useState } from 'react'
import type { OrderDetailResponse } from '@fitting-room/contracts'
import { Plus } from 'lucide-react'
import { useAssignees, useSetAccountingChecked } from '../../../lib/adminQueries'
import { assigneeName } from '../../../lib/appointments'
import { boutiqueDateTime } from '../../../lib/boutique'
import { useStaffToken } from '../../../lib/staffToken'
import { PaymentLedger } from './PaymentLedger'
import { PaymentStateBadge } from './PaymentStateBadge'
import { RecordPaymentDialog } from './RecordPaymentDialog'

/**
 * The order's payments: tperent's `OrderMoney` card, less the rental
 * buckets. The state, a way to record one, and the ledger. A canceled order
 * still takes a refund here — giving the deposit back usually comes after
 * the cancel.
 */
export function OrderPayments({ detail }: { readonly detail: OrderDetailResponse }) {
  const [recording, setRecording] = useState(false)
  const completed = detail.order.status === 'completed'
  return (
    <section className="st-card px-4 py-3" aria-labelledby="order-payments">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 id="order-payments" className="st-eyebrow">
            Payments
          </h2>
          {detail.order.status !== 'canceled' ? <PaymentStateBadge state={detail.money.paymentState} totalMinor={detail.money.totalMinor} paidMinor={detail.money.paidMinor} /> : null}
        </div>
        {!completed ? (
          <button type="button" className="st-btn-ghost" onClick={() => setRecording(true)}>
            <Plus size={14} aria-hidden />
            {detail.order.status === 'canceled' ? 'Record refund' : 'Record payment'}
          </button>
        ) : null}
      </div>
      <PaymentLedger payments={detail.payments} />
      <RecordPaymentDialog detail={detail} open={recording} onClose={() => setRecording(false)} />
    </section>
  )
}

/**
 * The owner's "accounting checked" mark. No process hangs off it yet
 * (`docs/22` § "Payments"); a stylist sees whether it is set, and only the
 * owner can set it.
 */
export function AccountingCard({ detail }: { readonly detail: OrderDetailResponse }) {
  const { token, role } = useStaffToken()
  const members = useAssignees(token).data?.members ?? []
  const set = useSetAccountingChecked(token, detail.order.id)
  const { accountingCheckedAt: at, accountingCheckedById: by } = detail.order
  const who = by ? assigneeName(members, by) : undefined
  const when = at ? `Checked ${boutiqueDateTime(at)}${who ? ` by ${who}` : ''}.` : 'Not checked yet.'

  return (
    <section className="st-card px-4 py-3">
      <h2 className="st-eyebrow mb-1.5">Accounting</h2>
      {role === 'owner' ? (
        <>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={Boolean(at)} disabled={set.isPending} onChange={(event) => set.mutate(event.target.checked)} />
            Accounting checked
          </label>
          <p className="st-meta mt-1 text-xs">{when}</p>
        </>
      ) : (
        <p className="text-sm">{when}</p>
      )}
      {set.isError ? (
        <p className="st-alert mt-2" role="alert">
          {set.error.message}
        </p>
      ) : null}
    </section>
  )
}
